
// tslint:disable-next-line:no-implicit-dependencies
import * as Aria from 'aria';
import { IManager, ILogHandler } from '@ms/telemetry-manager/lib/Manager';
import { ITelemetryHandler } from '@ms/telemetry-manager/lib/ITelemetryHandler';
import { IClonedEvent } from '@ms/telemetry-manager/lib/IClonedEvent';
import { ClonedEventType } from '@ms/telemetry-manager/lib/EventBase';
import { buildAriaEvent } from './AriaEventBuilder';
import * as ErrorHelper from '@ms/telemetry-manager/lib/ErrorHelper';
import { capitalize } from '@ms/utilities-strings/lib/index';

/**
 * Interface representing the common schema across all events reported to Aria.
 * Individual event types will add more fields, but this schema represents the set
 * of expected fields regardless of event type.
 */
export interface IAriaEventContext {
  // The full schema is defined here:
  // tslint:disable-next-line:max-line-length
  // https://onedrive.visualstudio.com/OneDrive%20Service/_git/Instrumentation?path=%2Fserver%2Fcosmos%2FProjects%2FSchematization%2FSchema%2Fschema.xml&version=GBmaster

  [key: string]: string | number | boolean | undefined;
  /**
   * Account type. Typically 'Consumer' or 'Business'.
   */
  AccountType?: string;
  /**
   * The internal app name of the host application registered with AAD.
   */
  AppName?: string;
  /**
   * The internal app id of the host application registered with AAD.
   */
  AppId?: string;
  /**
   * Whether or not the user's browser is a mobile browser.
   */
  BrowserIsMobile?: boolean;
  /**
   * The major version of the user's browser.
   */
  BrowserMajVer?: number;
  /**
   * The minor version of the user's browser.
   */
  BrowserMinVer?: string;
  /**
   * The name of the user's browser.
   */
  BrowserName?: string;
  /**
   * The original user agent for the user's browser.
   */
  BrowserUserAgent?: string;
  /**
   * The 'friendly' name of the host application.
   * e.g. 'Microsoft Office', 'Teams', 'OneDrive', 'SharePoint Team Sites', etc.
   */
  ClientName?: string;
  /**
   * The release environment of the host application.
   */
  Environment?: string;
  /**
   * The Farm label for SharePoint deployments.
   */
  FarmLabel?: string;
  /**
   * Whether or not a user is signed in.
   */
  IsAuthenticated?: 1 | 0;
  /**
   * The current market language for the application.
   */
  Market?: string;
  /**
   * The manifest name for applications which use manifest deployment.
   */
  Manifest?: string;
  /**
   * The name of the user's operating system.
   */
  OSName?: string;
  /**
   * The version of the user's operating system.
   */
  OSVersion?: string;
  /**
   * A guid tagging the current user session.
   */
  Session?: string;
  /**
   * The SharePoint tenant id for the user.
   */
  SiteSubscriptionId?: string;
  /**
   * The hex user PUID for the signed-in user from AAD.
   */
  UserId?: string;
  /**
   * The application build version.
   */
  Version?: string;
  /**
   * The SharePoint workload name, typically the name of the SharePoint product.
   */
  Workload?: string;
}

export interface IAriaOfflineCacheParams {
  getData(key: string): string;
  saveData(key: string, value: string): void;
}

export interface IAriaQosParams {
  isQosEvent(event: IClonedEvent): boolean;
  reportQos(qosInfo: {
    result: Aria._sender.CallbackEventType;
    statusCode: number;
    events: Aria.datamodels.Record[];
  }): void;
}

export type IAriaLogConfiguration = {
  [K in keyof Aria.LogConfiguration]?: Aria.LogConfiguration[K]
};

/**
 * Base parameters for the Aria telemetry handler.
 */
export interface IAriaTelemetryHandlerParams {
  ariaLoader: () => Promise<typeof Aria>;
  /**
   * The Aria tenant token to use for telemetry.
   * All events will be reported to the target tenant.
   */
  tenantToken: string;
  logStartEvents?: boolean;
  disableCookies?: boolean;
  ariaQos?: IAriaQosParams;
  context?: IAriaEventContext;
  offlineCache?: IAriaOfflineCacheParams;
  ariaLogConfiguration?: IAriaLogConfiguration;
}

/**
 * Telemetry handler for reporting events to Aria.
 * This handler ensures that events logged to Aria are well-formed and supplied with baseline required metadata fields.
 *
 * Host applications should generally use a class derived from this class, such as WebSharedAriaTelemtryHandler,
 * which supply default metadata extracted from context.
 */
export class AriaTelemetryHandler implements ITelemetryHandler {
  private _ariaLoader: () => Promise<typeof Aria>;

  private _tenantToken: string;
  private _logStartEvents: boolean;
  private _ariaQosParams: IAriaQosParams | undefined;
  private _ariaOfflineCacheParams: IAriaOfflineCacheParams | undefined;
  private _context: IAriaEventContext;
  private _disableCookies: boolean | undefined;
  private _logConfiguration: IAriaLogConfiguration | undefined;

  constructor(params: IAriaTelemetryHandlerParams) {
    const {
      logStartEvents = true,
      context = {}
    } = params;

    this._ariaLoader = params.ariaLoader;
    this._logStartEvents = logStartEvents;
    this._disableCookies = params.disableCookies;
    this._tenantToken = params.tenantToken;
    this._ariaQosParams = params.ariaQos;
    this._ariaOfflineCacheParams = params.offlineCache;
    this._context = context;
    this._logConfiguration = params.ariaLogConfiguration;
  }

  public attach(manager: IManager): () => void {
    let isDetached = false;

    let logHandler: ILogHandler;

    this._getAria().then((aria: typeof Aria) => {
      if (isDetached) {
        return;
      }

      logHandler = this._createLogHandler(aria);

      manager.addLogHandler(logHandler).forEach(logHandler);
    });

    return () => {
      isDetached = true;

      if (logHandler) {
        manager.removeLogHandler(logHandler);
      }
    };
  }

  private _getAria(): Promise<typeof Aria> {
    const loadAria = this._ariaLoader().then((aria: typeof Aria) => {
      this._initializeLogManager(aria);

      return aria;
    });

    this._getAria = () => loadAria;

    return loadAria;
  }

  private _createLogHandler(aria: typeof Aria): ILogHandler {
    const logger = this._createLogger(aria);

    const ariaQosParams = this._ariaQosParams;

    return (event: IClonedEvent): void => {
      // Try/catch individual events so that one bad event doesn't cause the rest to fail to get logged
      try {
        if (event.enabled && (event.eventType !== ClonedEventType.Start || this._logStartEvents) &&
          !(ariaQosParams && ariaQosParams.isQosEvent(event))) {
          const eventProperties = buildAriaEvent(aria, event);

          logger.logEvent(eventProperties);
        }
      } catch (exception) {
        let error;
        if (exception instanceof aria.Exception) {
          error = new Error(`Aria error: ${exception.toString()}`);
        }
        if (!exception || !exception.handled) {
          // Only log the exception if it was not already logged before
          ErrorHelper.log(error || exception);
        }
      }
    };
  }

  private _initializeLogManager(aria: typeof Aria): void {
    const ariaOfflineCacheParams = this._ariaOfflineCacheParams;

    if (!aria.LogManager.isInitialized()) {
      aria.LogManager.initialize(this._tenantToken, {
        disableCookies: this._disableCookies,
        ...(ariaOfflineCacheParams ? {
          browserOverrides: {
            onGetData: ariaOfflineCacheParams.getData,
            onSaveData: ariaOfflineCacheParams.saveData
          }
        } : {}),
        ...(this._logConfiguration || {})
      } as Aria.LogConfiguration);

      if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('beforeunload', (ev: BeforeUnloadEvent) => {
          aria.LogManager.flush();
        });
      }
    }

    const ariaQosParams = this._ariaQosParams;

    if (ariaQosParams) {
      aria.LogManager.addCallbackListener((
        callbackType: Aria._sender.CallbackEventType,
        statusCode: number,
        tenantToken: string,
        events: Aria.datamodels.Record[]
      ): void => {
        if (tenantToken !== this._tenantToken) {
          return;
        }

        ariaQosParams.reportQos({
          result: callbackType,
          statusCode: statusCode,
          events: events
        });
      });
    }
  }

  private _createLogger(aria: typeof Aria): Aria.Logger {
    const logger = new aria.Logger(this._tenantToken);

    const context = this._context;

    const {
      Session: session,
      Version: version,
      OSName: osName,
      OSVersion: osVersion,
      Market: market,
      UserId: userId,
      Manifest: manifest,
      ...otherContext
    } = context;

    const semanticContext: Aria.ISemanticContext & {
      contextMap?: { [key: string]: string | undefined; };
    } = logger.getSemanticContext();

    if (session) {
      logger.setContext('AppInfo.Session', session);
    }

    if (manifest) {
      logger.setContext('AppInfo.Manifest', manifest);
    }

    if (version) {
      semanticContext.setAppVersion(version);
    }

    semanticContext.setUserLanguage(market || '');

    if (userId) {
      semanticContext.setUserId(userId);
    }

    if (osName) {
      semanticContext.setDeviceOsName(osName);
    }

    if (osVersion) {
      semanticContext.setDeviceOsVersion(osVersion);
    }

    for (const key in otherContext) {
      if (Object.prototype.hasOwnProperty.call(otherContext, key)) {
        const value = otherContext[key];

        if (value === undefined || value === null) {
          continue;
        }

        const finalKey = capitalize(key);

        logger.setContext(finalKey, otherContext[key] as {});
      }
    }

    return logger;
  }
}
